/** biome-ignore-all lint/suspicious/noNonNullAssertedOptionalChain: happens with gql */
import { useApiQuery, useTeam } from '@vrplatform/data';
import { type Maybe, ensure, hasValue } from '@vrplatform/utils';
import { randomId, useDebouncedValue } from '@mantine/hooks';
import type { activeStatus_enum, party_enum } from '@vrplatform/graphql';
import { getReservationFinancialGroup } from '../_utils';
import {
  getLedgerReservationDetailCurrency,
  getReservationAdjustmentPostingAtByLineId,
  mapReservationFeeRows,
} from './useLedgerReservationDetailDrawerQuery.utils';

const RESERVATION_DRAWER_QUERY_KEY = 'reservation-detail-drawer';
export const STORYBOOK_LEDGER_RESERVATION_DETAIL_DRAWER_QUERY_KEY =
  RESERVATION_DRAWER_QUERY_KEY;

export function useLedgerReservationDetailDrawerQuery(id: Maybe<string>) {
  const [{ id: teamId, currency: teamCurrency }] = useTeam();

  const query = useApiQuery(
    async ($api, signal, { id, teamId }, $hasura) => {
      if (!id) throw new Error('Missing reservation id');

      const [reservation, dbResult] = await Promise.all([
        $api
          .GET('/reservations/{id}', {
            params: {
              path: {
                id,
              },
            },
            signal,
          })
          .then((result) => {
            if (result.error) {
              if (result.error.code === 'NOT_FOUND') return null;
              else throw result.error;
            }
            return result.data;
          }),
        $hasura(
          (q) => {
            const ownerStatementIds = q
              .journalEntries({
                where: {
                  tenantId: {
                    _eq: teamId,
                  },
                  reservationId: {
                    _eq: id,
                  },
                  attachedToOwnerStatementId: {
                    _is_null: false,
                  },
                },
                distinct_on: ['attachedToOwnerStatementId'],
              })
              .map((x) => x.attachedToOwnerStatementId!);

            const depositConnections = q
              .transactions({
                where: {
                  lines: {
                    reservationId: {
                      _eq: id,
                    },
                  },
                  connectionId: {
                    _is_null: false,
                  },
                  type: { _eq: 'deposit' },
                },
              })
              .map((x) => ({
                id: x.connection?.id!,
                transactionId: x.id,
                name: x.connection?.name!,
                app: {
                  id: x.connection?.app?.id!,
                  name: x.connection?.app?.name!,
                  icon: x.connection?.app?.iconRound!,
                },
              }));

            return {
              ownerStatementIds,
              depositConnections,
            };
          },
          {
            fetchOptions: {
              signal,
            },
          }
        ),
      ]);

      if (!reservation)
        return {
          reservation: null,
        };

      type PostingDatePreview = {
        hasMore: boolean;
        entries: {
          postingDate: string;
          amount: number;
        }[];
      };

      type apiFinancialLine = NonNullable<
        typeof reservation.financials.lines
      >[number] & {
        postingDatePreview?: PostingDatePreview | null;
      };

      type ReservationFinancialLine = {
        id: string;
        description: string;
        status: apiFinancialLine['status'];
        reservationLine: apiFinancialLine['line'];
        recurringFee?: apiFinancialLine['recurringFee'];
        account: apiFinancialLine['account'];
        postingAt?: string | null;
        postingDatePreview?: apiFinancialLine['postingDatePreview'];
        type: 'adjustment' | 'financial';
        centTotal: number;
        lock: apiFinancialLine['lock'];
      };

      type FeeFinancialLine = {
        id: string;
        description: string;
        status: apiFinancialLine['status'];
        reservationLine?: apiFinancialLine['line'];
        recurringFee: apiFinancialLine['recurringFee'];
        account: apiFinancialLine['account'];
        postingAt?: string | null;
        postingDatePreview?: apiFinancialLine['postingDatePreview'];
        type: 'adjustment' | 'financial';
        centTotal: number;
        lock: apiFinancialLine['lock'];
      };

      type Expense = {
        id: string;
        status: activeStatus_enum;
        centTotal: number;
        description: string;
        party: party_enum | undefined | null;
        expense: {
          id: string;
          description: string;
          currency: string;
          date: any;
        };
      };

      const currency = getLedgerReservationDetailCurrency({
        teamCurrency,
        reservationCurrency: reservation.currency,
      });
      const financialLines = reservation.financials?.lines as
        | apiFinancialLine[]
        | undefined;

      const fees: FeeFinancialLine[] = mapReservationFeeRows({
        reservation,
        financialLines,
        createId: randomId,
      });

      const expenses: Expense[] =
        financialLines
          ?.map<Expense | null>((line) => {
            if (
              !line.transaction ||
              getReservationFinancialGroup(line) !== 'expense'
            )
              return null;

            return {
              id: line.transaction.id,
              centTotal: line.amount,
              description: line.name,
              party: line.party,
              status: line.status,
              expense: {
                currency: line.transaction.currency,
                date: line.transaction.date,
                description: line.transaction.description || '-',
                id: line.transaction.id,
              },
            };
          })
          .filter(hasValue) ?? [];

      type Deposit = {
        id: string;
        description: string;
        currency: string;
        date: string;
        centTotal: number;
        status: activeStatus_enum;
        connection: {
          id: string;
          name: string;
          app:
            | {
                id: string;
                name: string;
                icon?: Maybe<string>;
              }
            | undefined;
        } | null;
      };

      const deposits: Deposit[] =
        financialLines
          ?.filter((line) => {
            return (
              getReservationFinancialGroup(line) === 'deposit' &&
              line.transaction &&
              line.type &&
              ensure<NonNullable<(typeof line)['type']>[]>([
                'transaction_deposit',
                'transaction_deposit_line',
                'transaction_deposit_refund',
              ]).includes(line.type)
            );
          })
          .reduce<Deposit[]>((acc, line) => {
            const transaction = line.transaction;
            if (!transaction) return acc;

            const index = acc.findIndex((d) => d.id === transaction.id);

            if (index === -1) {
              const connection = dbResult.depositConnections.find(
                (c) => c.transactionId === transaction.id
              );

              acc.push({
                id: transaction.id,
                centTotal: line.amount,
                status: line.status,
                description: transaction?.description || '-',
                currency: transaction.currency,
                date: transaction.date,
                connection: connection
                  ? {
                      id: connection.id,
                      name: connection.name,
                      app: connection.app,
                    }
                  : null,
              });
            } else {
              acc[index].centTotal += line.amount;
            }

            return acc;
          }, []) ?? [];

      const financials: ReservationFinancialLine[] =
        financialLines
          ?.map<ReservationFinancialLine | null>((line) => {
            const isFinancials =
              line.party === 'owners' &&
              getReservationFinancialGroup(line) === 'financial';

            if (!isFinancials) return null;

            const isAdjustment =
              line.type === 'reservation_adjustment' ||
              line.type === 'reservation_adjustment_cancellation';

            return {
              id: randomId(),
              description: line.name,
              centTotal: line.amount,
              reservationLine: line.line,
              status: line.status,
              account: line.account,
              postingAt: getReservationAdjustmentPostingAtByLineId(
                reservation,
                line.line?.id,
                line.postingDatePreview
              ),
              postingDatePreview: line.postingDatePreview,
              type: isAdjustment ? 'adjustment' : 'financial',
              lock: line.lock,
            };
          })
          .filter(hasValue)
          .sort((a, b) => {
            const aIsAdjustment = a.type === 'adjustment';
            const bIsAdjustment = b.type === 'adjustment';

            if (aIsAdjustment !== bIsAdjustment) {
              return aIsAdjustment ? 1 : -1;
            }

            if (b.centTotal !== a.centTotal) {
              return b.centTotal - a.centTotal;
            }

            return a.description.localeCompare(b.description);
          }) ?? [];

      const issues =
        reservation.issues?.sort((a, b) => {
          const order: Record<NonNullable<(typeof a)['code']> | '-', number> = {
            priorToStartDate: 0,
            inactiveConnection: 2,
            inactiveListing: 3,
            unassignedAccount: 4,
            lineNotFound: 5,
            unbalancedJournalEntries: 6,
            guestTotalsZero: 7,
            guestTotalsMismatch: 8,
            listingOwnershipPeriodNotFound: 9,
            '-': 1000,
          };

          return (order[a.code ?? '-'] ?? 0) - (order[b.code ?? '-'] ?? 0);
        }) ?? [];

      return {
        reservation: {
          ...reservation,
          lock: reservation.lock
            ? {
                ...reservation.lock,
                message: reservation.lock.booksClosed
                  ? 'The reservation falls into your book closed period. Reopen the books before updating.'
                  : 'The reservation falls into a period where you have a related published owner statement. Unpublish the statement to update the reservation.',
              }
            : undefined,
          issues,
          generalLedgerPostingAt: reservation?.generalLedgerPostingAt ?? null,
          totals: reservation.financials?.totals,
          ownerStatementIds: dbResult.ownerStatementIds,
          financials,
          feesAndCommissions: fees,
          expenses,
          deposits,
          status: reservation.status,
          currency,
          isManualReservation: reservation.connection?.appId === 'finalytic',
          isCancelledAndPending:
            reservation.status === 'canceled' &&
            reservation.payment.status !== 'paid',
        },
      };
    },
    {
      queryGroup: 'reservations',
      uniqueQueryKey: 'reservation-detail-drawer',
      queryKeys: [RESERVATION_DRAWER_QUERY_KEY, teamId],
      skip: !id || !teamId,
      hideNotification: true,
      variables: {
        id,
        teamId,
        teamCurrency,
      },
      storybookQueryKey: STORYBOOK_LEDGER_RESERVATION_DETAIL_DRAWER_QUERY_KEY,
    }
  );

  const [debounced] = useDebouncedValue(query.data, 500);

  return { ...query, data: query.data || debounced };
}

export type Reservation = NonNullable<
  NonNullable<
    ReturnType<typeof useLedgerReservationDetailDrawerQuery>['data']
  >['reservation']
>;
